// Sign-up's two steps after the first sign-in (Console D entry screens, APS-323): an organization
// on a 14-day trial (D47, D107), then its first project (D6). Apps and keys come next, on the
// first-run page. An organization created without a trial (its domain's or its account's was
// used, APS-249) says so on the project step and ends on Settings › Plan. Why a step was refused
// sits above its form; a member, who cannot create a project, sees why instead of the form.
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { type FormEvent, type ReactNode, useId, useState } from 'react'
import { isApiError } from '../api/client'
import { meQuery, projectsQuery, useCreateOrg, useCreateProject } from '../api/queries'
import { EntryHead, EntryNote, EntryPage, useToast } from '../components/d'
import { ProjectDeletions, useProjectDeletions } from '../components/ProjectDeletions'
import { useI18n } from '../i18n'
import { useFlash } from '../lib/flash'
import type { PlanSearch } from './search'

/** A step's one window: the name, its hint, the button, and what comes after. */
function NameForm({
  label,
  hint,
  submit,
  busy,
  after,
  onSubmit,
}: {
  label: string
  hint?: string
  submit: string
  busy: boolean
  after?: ReactNode
  onSubmit: (name: string) => void
}) {
  const [name, setName] = useState('')
  const id = useId()
  const hintId = useId()
  return (
    <form
      className="en-win"
      onSubmit={(e: FormEvent) => {
        e.preventDefault()
        if (name.trim()) onSubmit(name.trim())
      }}
    >
      <div className="en-lbl">
        <label htmlFor={id}>{label}</label>
        <input
          id={id}
          className="input en-in"
          required
          maxLength={100}
          value={name}
          onChange={(e) => setName(e.target.value)}
          {...(hint ? { 'aria-describedby': hintId } : {})}
        />
        {hint ? (
          <span className="en-hint" id={hintId}>
            {hint}
          </span>
        ) : null}
      </div>
      <button type="submit" className="btn solid en-full" disabled={busy}>
        {submit}
      </button>
      {after ? <p className="en-hint">{after}</p> : null}
    </form>
  )
}

export function WelcomePage() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const create = useCreateOrg()
  const refusal: [string, 'warn' | 'crash'] | undefined = create.error
    ? isApiError(create.error, 403)
      ? create.error.code === 'disposable_email'
        ? [t('welcome.disposable'), 'crash']
        : create.error.code === 'free_paused'
          ? [t('welcome.freePaused'), 'warn']
          : [t('welcome.verify'), 'warn']
      : isApiError(create.error, 429)
        ? create.error.code === 'network_rate_limited'
          ? [t('welcome.networkLimited'), 'warn']
          : [t('welcome.rateLimited'), 'warn']
        : [t('state.failed'), 'crash']
    : undefined
  return (
    <EntryPage>
      <EntryHead kicker={t('welcome.kicker')} title={t('welcome.title')} lede={t('welcome.lede')} />
      {refusal ? <EntryNote tone={refusal[1]}>{refusal[0]}</EntryNote> : null}
      <NameForm
        label={t('welcome.orgName')}
        hint={t('welcome.orgHint')}
        submit={t('welcome.submit')}
        busy={create.isPending}
        onSubmit={(name) =>
          create.mutate(name, {
            onSuccess: (org) =>
              navigate({
                to: '/o/$orgId/new-project',
                params: { orgId: org.id },
                search: {
                  // Without a trial (APS-249), the project step says why and ends on the plan.
                  ...(org.trial !== 'granted' ? { trial: org.trial } : {}),
                  ...(org.trial_domain ? { domain: org.trial_domain } : {}),
                },
              }),
          })
        }
      />
    </EntryPage>
  )
}

export function NewProjectPage() {
  const { t } = useI18n()
  const { orgId } = useParams({ strict: false }) as { orgId: string }
  const { trial, domain } = useSearch({ strict: false }) as PlanSearch
  const navigate = useNavigate()
  const create = useCreateProject(orgId)
  const me = useQuery({ ...meQuery, throwOnError: false })
  const org = me.data?.orgs.find((o) => o.id === orgId)
  // A member cannot create a project (owners and admins can): why, instead of the form.
  const member = org?.role === 'member' || isApiError(create.error, 403)
  const planLimit = isApiError(create.error, 402)
  // The plan page lives in a project: the organization's first, which the limit says exists.
  const projects = useQuery({ ...projectsQuery(orgId), enabled: planLimit })
  const first = projects.data?.[0]
  const toPlan = !!trial
  // An organization whose last project is being deleted lands here: it is listed, with Cancel.
  const deletions = useProjectDeletions(orgId, !!org)
  const deleting = deletions.data?.erasures ?? []
  // A deletion scheduled from the last project's page, or canceled here: its toast (flash.ts).
  const { toast, show } = useToast()
  useFlash(show)
  return (
    <EntryPage>
      <EntryHead
        kicker={t('newProject.kicker')}
        title={t('newProject.title')}
        lede={t('newProject.lede')}
      />
      {trial === 'domain_used' && domain ? (
        <EntryNote title={t('newProject.trialDomainUsed', { domain })}>
          {t('newProject.trialDomainUsedBody')}
        </EntryNote>
      ) : trial ? (
        <EntryNote title={t('newProject.trialAccountUsed')}>
          {t('newProject.trialAccountUsedBody')}
        </EntryNote>
      ) : null}
      {deleting.length > 0 ? (
        <EntryNote
          tone="mute"
          glyph="i"
          title={t('settings.general.deleting.title')}
          after={
            <ProjectDeletions
              orgId={orgId}
              erasures={deleting}
              canCancel={!member}
              onCanceled={(e) =>
                navigate({
                  to: '/o/$orgId/p/$projectId/home',
                  params: { orgId, projectId: e.project_id },
                })
              }
            />
          }
        >
          {t('newProject.deleting')}
        </EntryNote>
      ) : null}
      {member ? (
        <EntryNote tone="mute" glyph="!">
          {t('newProject.forbidden', { name: org?.name ?? '' })}
        </EntryNote>
      ) : planLimit ? (
        <EntryNote>
          {t('newProject.planLimit')}{' '}
          {first ? (
            <Link
              className="en-ul"
              to="/o/$orgId/p/$projectId/settings/$section"
              params={{ orgId, projectId: first.id, section: 'plan' }}
            >
              {t('newProject.openPlan')}
            </Link>
          ) : null}
        </EntryNote>
      ) : create.error ? (
        <EntryNote tone="crash">{t('state.failed')}</EntryNote>
      ) : null}
      {member ? null : (
        <NameForm
          label={t('newProject.name')}
          hint={t('newProject.hint')}
          submit={t('newProject.submit')}
          busy={create.isPending}
          after={toPlan ? undefined : t('newProject.step3')}
          onSubmit={(name) =>
            create.mutate(name, {
              onSuccess: (project) =>
                toPlan
                  ? navigate({
                      to: '/o/$orgId/p/$projectId/settings/$section',
                      params: { orgId, projectId: project.id, section: 'plan' },
                    })
                  : navigate({
                      to: '/o/$orgId/p/$projectId/setup',
                      params: { orgId, projectId: project.id },
                    }),
            })
          }
        />
      )}
      {toast}
    </EntryPage>
  )
}
